import { useState, useEffect } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Loader2, ArrowLeft, Zap, Sparkles, Phone, StickyNote, CheckSquare, DollarSign, BarChart3, Clock, Plus, Check, CheckCircle, AlertTriangle, TrendingUp, Flame, Crosshair, Globe, Trophy, XCircle, Download, FileText, Pencil, X, Save, Trash2, Mail, Gift, Rocket, Send, Search } from "lucide-react";
import { motion } from "framer-motion";

import CloseReasonModal from "./CloseReasonModal";
import ProposalBuilder from "./ProposalBuilder";
import ReportEditor from "./ReportEditor";
import BlueprintAnswersEditor from "./BlueprintAnswersEditor";
import { TasksPanel, ActivityTimeline, DealScoreBreakdown, DealSnapshot } from "./LeadDetailPanels";
import { INDUSTRIES } from "../../data/industries";

const STATUS_LABELS = { new: "New", contacted: "Contacted", booked: "Booked", qualified: "Qualified", proposal: "Proposal", closed_won: "Won", closed_lost: "Lost" };
const STATUS_OPTS = ["new", "contacted", "booked", "qualified", "proposal", "closed_won", "closed_lost"];

export default function OpsLeadDetail({ leadId, onBack }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [taskText, setTaskText] = useState("");
  const [showCallForm, setShowCallForm] = useState(false);
  const [callForm, setCallForm] = useState({ date: "", call_type: "discovery", notes: "" });
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [billingForm, setBillingForm] = useState({ amount: "", bill_type: "audit", status: "pending" });
  const [compLoading, setCompLoading] = useState(false);
  const [closeModal, setCloseModal] = useState(null); // { isWin }
  const [pdfLoading, setPdfLoading] = useState(false);
  const [deckLoading, setDeckLoading] = useState(false);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [showProposalBuilder, setShowProposalBuilder] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [outreachSending, setOutreachSending] = useState(false);
  const [outreachSent, setOutreachSent] = useState(false);
  const [compSending, setCompSending] = useState(false);
  const [compSent, setCompSent] = useState(false);
  const [showCompConfirm, setShowCompConfirm] = useState(false);
  const [compReason, setCompReason] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitePrice, setInvitePrice] = useState("4495");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);

  // Derive sent states from lead data
  const hasCompSent = lead ? (lead.tags || []).includes("comp-blueprint") : false;
  const hasOutreachSent = lead ? (lead.activity || []).some(a => a.type === "email_sent" && a.description?.includes("Sales outreach")) : false;
  const hasInviteSent = lead ? (lead.tags || []).includes("blueprint-invited") : false;
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadLead = async () => {
    try {
      const { data } = await api.get(`/ops/leads/${leadId}`);
      setLead(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadLead(); }, [leadId]); // eslint-disable-line

  const updateStatus = async (status, closeData = null) => {
    if ((status === "closed_won" || status === "closed_lost") && !closeData) {
      setCloseModal({ isWin: status === "closed_won" });
      return;
    }
    await api.put(`/ops/leads/${leadId}`, { status, ...closeData });
    loadLead();
  };

  const handleCloseConfirm = async (closeData) => {
    if (!closeModal) return;
    const status = closeModal.isWin ? "closed_won" : "closed_lost";
    await updateStatus(status, closeData);
    setCloseModal(null);
  };

  const updateDealValue = async (val) => {
    await api.put(`/ops/leads/${leadId}`, { deal_value: parseFloat(val) || 0 });
    loadLead();
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      await api.post("/ops/ai/generate", { lead_id: leadId });
      loadLead();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to generate AI intelligence";
      alert(msg);
    }
    finally { setAiLoading(false); }
  };

  const generateCompetitors = async () => {
    setCompLoading(true);
    try {
      await api.post("/ops/ai/competitors", { lead_id: leadId });
      loadLead();
    } catch (err) { console.error(err); }
    finally { setCompLoading(false); }
  };

  const downloadPDF = async () => {
    if (!lead.audit_id) return;
    setPdfLoading(true);
    try {
      const response = await api.get(`/report/${lead.audit_id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Stansbury_AI_Report_${(lead.company || "Report").replace(/\s/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("PDF download failed:", err); }
    finally { setPdfLoading(false); }
  };

  const downloadDeck = async () => {
    if (!lead.audit_id) return;
    setDeckLoading(true);
    try {
      const response = await api.get(`/report/${lead.audit_id}/deck`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Stansbury_AI_Exec_Deck_${(lead.company || "Deck").replace(/\s/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Deck download failed:", err); }
    finally { setDeckLoading(false); }
  };

  const downloadProposal = () => {
    setShowProposalBuilder(true);
  };

  const handleUpgradeWhiteGlove = async () => {
    if (!upgradeReason.trim()) return;
    setUpgrading(true);
    try {
      await api.post(`/ops/leads/${leadId}/upgrade-white-glove`, { reason: upgradeReason });
      setShowUpgradeModal(false);
      setUpgradeReason("");
      loadLead();
    } catch (err) {
      const msg = err.response?.data?.detail || "Upgrade failed";
      alert(msg);
    }
    finally { setUpgrading(false); }
  };

  const generateReport = async () => {
    if (!lead.audit_id) return;
    setReportGenerating(true);
    try {
      await api.post(`/report/generate/${lead.audit_id}`);
      loadLead();
    } catch (err) { console.error("Report generation failed:", err); }
    finally { setReportGenerating(false); }
  };

  const generateWhiteGlove = async () => {
    if (!lead.audit_id) return;
    setReportGenerating(true);
    try {
      await api.post(`/report/generate/${lead.audit_id}/white-glove`);
      loadLead();
    } catch (err) { console.error("Report generation failed:", err); }
    finally { setReportGenerating(false); }
  };

  const sendOutreach = async () => {
    setOutreachSending(true);
    try {
      await api.post(`/ops/leads/${leadId}/outreach`);
      setOutreachSent(true);
      loadLead();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send";
      alert(msg);
    }
    finally { setOutreachSending(false); }
  };

  const sendCompBlueprint = async () => {
    if (!compReason.trim()) return;
    setCompSending(true);
    try {
      await api.post(`/ops/leads/${leadId}/comp-blueprint`, { reason: compReason });
      setCompSent(true);
      setShowCompConfirm(false);
      loadLead();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send";
      alert(msg);
    }
    finally { setCompSending(false); }
  };

  const sendBlueprintInvite = async () => {
    setInviteSending(true);
    try {
      await api.post(`/ops/leads/${leadId}/send-blueprint-invite`, { price: parseFloat(invitePrice) || 0, message: inviteMessage });
      setInviteSent(true);
      setShowInviteModal(false);
      loadLead();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send invite");
    }
    finally { setInviteSending(false); }
  };

  const startEditing = () => {
    setEditForm({
      name: lead.name || "",
      company: lead.company || "",
      role: lead.role || "",
      email: lead.email || "",
      industry: lead.industry || "",
      sub_industry: lead.sub_industry || "",
      website: lead.website || "",
      business_description: lead.business_description || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/ops/leads/${leadId}`, editForm);
      setEditing(false);
      loadLead();
    } catch (err) { console.error(err); }
  };

  const deleteLead = async () => {
    setDeleting(true);
    try {
      await api.delete(`/ops/leads/${leadId}`);
      onBack();
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.post("/ops/notes", { lead_id: leadId, content: noteText });
      setNoteText("");
      loadLead();
    } catch (err) { console.error(err); }
    finally { setSavingNote(false); }
  };

  const addTask = async () => {
    if (!taskText.trim()) return;
    await api.post("/ops/tasks", { lead_id: leadId, title: taskText });
    setTaskText("");
    loadLead();
  };

  const toggleTask = async (taskId, completed) => {
    await api.put(`/ops/tasks/${taskId}`, { completed: !completed });
    loadLead();
  };

  const addCall = async () => {
    if (!callForm.date) return;
    await api.post("/ops/calls", { lead_id: leadId, ...callForm, status: "scheduled" });
    setCallForm({ date: "", call_type: "discovery", notes: "" });
    setShowCallForm(false);
    loadLead();
  };

  const updateCall = async (callId, updates) => {
    await api.put(`/ops/calls/${callId}`, updates);
    loadLead();
  };

  const addBilling = async () => {
    if (!billingForm.amount) return;
    await api.post("/ops/billing", { lead_id: leadId, amount: parseFloat(billingForm.amount), bill_type: billingForm.bill_type, status: billingForm.status });
    setBillingForm({ amount: "", bill_type: "audit", status: "pending" });
    setShowBillingForm(false);
    loadLead();
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0033CC]" /></div>;
  if (!lead) return <div className="p-8 text-center text-slate-400">Lead not found</div>;

  const intel = lead.ai_intelligence;
  const scores = lead.audit_data?.scores || {};
  const sections = scores.sections || {};

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="ops-lead-detail">
      {/* Header */}
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-12" />
            <span className="text-xs font-['JetBrains_Mono'] px-2 py-0.5 bg-[#0033CC] text-white">OPS</span>
          </div>
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 font-['Outfit']" data-testid="ops-lead-back"><ArrowLeft className="w-4 h-4" /> Back to Pipeline</button>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-6">
        {/* Lead Header */}
        <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="lead-header">
          <div className="flex flex-wrap items-start justify-between gap-4">
            {editing ? (
              /* ── Edit Mode ── */
              <div className="flex-1 space-y-3" data-testid="lead-edit-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Name</Label>
                    <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="rounded-none border-slate-300 h-9 text-sm" data-testid="edit-lead-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Company</Label>
                    <Input value={editForm.company} onChange={e => setEditForm({ ...editForm, company: e.target.value })} className="rounded-none border-slate-300 h-9 text-sm" data-testid="edit-lead-company" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Role</Label>
                    <Input value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} placeholder="CEO, CMO..." className="rounded-none border-slate-300 h-9 text-sm" data-testid="edit-lead-role" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Email</Label>
                    <Input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="rounded-none border-slate-300 h-9 text-sm" data-testid="edit-lead-email" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Industry</Label>
                    <Select value={editForm.industry} onValueChange={v => setEditForm({ ...editForm, industry: v, sub_industry: "" })}>
                      <SelectTrigger className="rounded-none border-slate-300 h-9 text-sm" data-testid="edit-lead-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map(ind => <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Sector</Label>
                    <Select value={editForm.sub_industry} onValueChange={v => setEditForm({ ...editForm, sub_industry: v })}>
                      <SelectTrigger className="rounded-none border-slate-300 h-9 text-sm" data-testid="edit-lead-sub-industry"><SelectValue placeholder={editForm.industry ? "Select sector" : "Select industry first"} /></SelectTrigger>
                      <SelectContent>
                        {(INDUSTRIES.find(i => i.value === editForm.industry)?.subs || []).map(sub => <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400 mb-1 block">Website</Label>
                  <Input value={editForm.website} onChange={e => setEditForm({ ...editForm, website: e.target.value })} placeholder="https://..." className="rounded-none border-slate-300 h-9 text-sm" data-testid="edit-lead-website" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400 mb-1 block">Business Description</Label>
                  <Textarea value={editForm.business_description} onChange={e => setEditForm({ ...editForm, business_description: e.target.value })} placeholder="What does this company do?" className="rounded-none border-slate-300 text-sm min-h-[60px]" data-testid="edit-lead-desc" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <Button onClick={saveEdit} size="sm" className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-8 text-xs font-['Outfit']" data-testid="save-lead-edit"><Save className="w-3 h-3 mr-1" />Save</Button>
                    <Button onClick={() => setEditing(false)} variant="outline" size="sm" className="rounded-none h-8 text-xs font-['Outfit']" data-testid="cancel-lead-edit">Cancel</Button>
                  </div>
                  <Button onClick={() => setShowDeleteConfirm(true)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-none h-8 text-xs font-['Outfit']" data-testid="delete-lead-btn">
                    <Trash2 className="w-3 h-3 mr-1" />Delete Lead
                  </Button>
                </div>
              </div>
            ) : (
              /* ── View Mode ── */
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A]">{lead.name}</h1>
                  <button onClick={startEditing} className="text-slate-400 hover:text-[#0033CC] transition-colors p-1" data-testid="edit-lead-btn"><Pencil className="w-3.5 h-3.5" /></button>
                </div>
                <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">{lead.company} {lead.role ? `/ ${lead.role}` : ""}</p>
                <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mt-1">{lead.email} {lead.industry ? ` · ${lead.industry.replace(/_/g, " ")}` : ""} · Source: {(lead.source || "").replace(/_/g, " ")}</p>
                {lead.website && <p className="text-xs text-[#0033CC] font-['IBM_Plex_Sans'] mt-0.5"><a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{lead.website}</a></p>}
                {lead.business_description && <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mt-1 max-w-xl">{lead.business_description}</p>}
                {/* Sales outreach button — only show if no audit/payment */}
                {lead.email && (
                  <div className="mt-3 flex gap-2">
                    <Button onClick={() => setShowInviteModal(true)} disabled={inviteSent || hasInviteSent} size="sm" className={`rounded-none h-8 text-xs ${hasInviteSent ? "bg-emerald-100 text-emerald-700 cursor-not-allowed" : "bg-[#0033CC] text-white hover:bg-[#002299]"} font-['Outfit']`} data-testid="send-blueprint-invite-btn">
                      {(inviteSent || hasInviteSent) ? <><CheckCircle className="w-3 h-3 mr-1" />Invite Sent</> : <><Send className="w-3 h-3 mr-1" />Send Blueprint Invite</>}
                    </Button>
                    {!lead.audit_id && (
                      <>
                        <Button onClick={sendOutreach} disabled={outreachSending || outreachSent || hasOutreachSent} size="sm" variant="outline" className={`rounded-none h-8 text-xs ${hasOutreachSent ? "border-slate-300 text-slate-400 cursor-not-allowed" : "border-[#0033CC] text-[#0033CC] hover:bg-[#0033CC] hover:text-white"} font-['Outfit'] transition-colors`} data-testid="send-outreach-btn">
                          {(outreachSent || hasOutreachSent) ? <><CheckCircle className="w-3 h-3 mr-1 text-emerald-600" />Outreach Sent</> : outreachSending ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Sending...</> : <><Mail className="w-3 h-3 mr-1" />Send Sales Email</>}
                        </Button>
                        <Button onClick={() => setShowCompConfirm(true)} disabled={compSending || compSent || hasCompSent} size="sm" variant="outline" className={`rounded-none h-8 text-xs ${hasCompSent ? "border-slate-300 text-slate-400 cursor-not-allowed" : "border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white"} font-['Outfit'] transition-colors`} data-testid="send-comp-btn">
                          {(compSent || hasCompSent) ? <><CheckCircle className="w-3 h-3 mr-1 text-emerald-600" />Comp Sent</> : compSending ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Sending...</> : <><Gift className="w-3 h-3 mr-1" />Send Comp Blueprint</>}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-3">
              <div>
                <Label className="text-xs text-slate-400 mb-1 block">Status</Label>
                <Select value={lead.status} onValueChange={updateStatus}>
                  <SelectTrigger className="w-40 rounded-none border-slate-300 h-9 text-sm" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTS.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400 mb-1 block">Deal Value</Label>
                <Input type="number" value={lead.deal_value || ""} onBlur={(e) => updateDealValue(e.target.value)} onChange={() => {}} placeholder="$0" className="w-32 rounded-none border-slate-300 h-9 text-sm font-['JetBrains_Mono']" data-testid="lead-deal-value" />
              </div>
              {lead.score > 0 && (
                <div className="text-center px-4">
                  <div className="text-2xl font-['Outfit'] font-light text-[#0033CC]">{lead.score}</div>
                  <p className="text-xs text-slate-400 font-['JetBrains_Mono']">Score</p>
                </div>
              )}
              {lead.deal_score > 0 && (
                <div className="text-center px-4 border-l border-slate-200">
                  <div className={`text-2xl font-['Outfit'] font-bold flex items-center justify-center gap-1 ${lead.deal_score >= 60 ? "text-red-600" : lead.deal_score >= 40 ? "text-amber-600" : "text-slate-600"}`}>
                    {lead.deal_score >= 60 && <Flame className="w-5 h-5" />}{lead.deal_score}
                  </div>
                  <p className="text-xs text-slate-400 font-['JetBrains_Mono']">Deal Score</p>
                </div>
              )}
              {lead.audit_data?.report?.tier && (
                <div className="text-center px-4 border-l border-slate-200">
                  <div className="text-sm font-['JetBrains_Mono'] font-bold px-3 py-1 bg-[#0033CC]/10 text-[#0033CC]">
                    Blueprint
                  </div>
                  <p className="text-xs text-slate-400 font-['JetBrains_Mono'] mt-1">Report Generated</p>
                </div>
              )}
            </div>
          </div>
          {/* Close Reason Banner */}
          {(lead.status === "closed_won" || lead.status === "closed_lost") && lead.close_reason && (
            <div className={`mt-4 p-3 border ${lead.status === "closed_won" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="close-reason-banner">
              <div className="flex items-center gap-2 mb-1">
                {lead.status === "closed_won" ? <Trophy className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                <span className={`text-sm font-['Outfit'] font-medium ${lead.status === "closed_won" ? "text-emerald-800" : "text-red-800"}`}>
                  {lead.status === "closed_won" ? "Deal Won" : "Deal Lost"} — {(lead.close_reason || "").replace(/_/g, " ")}
                </span>
                {lead.closed_at && <span className="text-xs font-['JetBrains_Mono'] text-slate-400 ml-auto">{lead.closed_at.split("T")[0]}</span>}
              </div>
              {lead.close_notes && <p className="text-xs text-slate-600 font-['IBM_Plex_Sans'] ml-6">{lead.close_notes}</p>}
              {lead.close_category && <span className="text-[10px] font-['JetBrains_Mono'] px-2 py-0.5 bg-white/60 text-slate-500 ml-6 mt-1 inline-block">{(lead.close_category || "").replace(/_/g, " ")}</span>}
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" data-testid="lead-tabs">
          <TabsList className="bg-white border border-slate-200 rounded-none p-1 mb-6 flex-wrap h-auto">
            {[
              { v: "overview", icon: BarChart3, label: "Overview" },
              { v: "intelligence", icon: Sparkles, label: "AI Intelligence" },
              { v: "competitors", icon: Crosshair, label: "Competitors" },
              { v: "discovery", icon: Search, label: "Discovery Answers" },
              { v: "audit", icon: TrendingUp, label: "Blueprint Data" },
              { v: "calls", icon: Phone, label: "Calls" },
              { v: "notes", icon: StickyNote, label: "Notes" },
              { v: "billing", icon: DollarSign, label: "Billing" },
            ].map(t => (
              <TabsTrigger key={t.v} value={t.v} className="rounded-none font-['Outfit'] text-sm data-[state=active]:bg-[#0033CC] data-[state=active]:text-white">
                <t.icon className="w-3.5 h-3.5 mr-1.5" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <TasksPanel tasks={lead.tasks} taskText={taskText} setTaskText={setTaskText} addTask={addTask} toggleTask={toggleTask} />
                <ActivityTimeline activity={lead.activity} />
              </div>
              <div className="space-y-6">
                <DealSnapshot intel={intel} generateAI={generateAI} aiLoading={aiLoading} />
                {lead.deal_score_components && <DealScoreBreakdown dealScore={lead.deal_score} components={lead.deal_score_components} />}
              </div>
            </div>
          </TabsContent>

          {/* AI INTELLIGENCE */}
          <TabsContent value="intelligence">
            <div className="space-y-6" data-testid="lead-intelligence">
              {!intel ? (
                <div className="bg-white border border-slate-200 p-10 text-center">
                  <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-['Outfit'] text-[#0F172A] mb-2">Generate Sales Intelligence</h3>
                  <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-4">AI will analyze this company's blueprint, website, and business context to generate actionable deal strategy.</p>
                  <Button onClick={generateAI} disabled={aiLoading} className="bg-[#0033CC] text-white rounded-none px-8 font-['Outfit']" data-testid="generate-ai-full-btn">
                    {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyzing company...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate AI Intelligence</>}
                  </Button>
                </div>
              ) : (
                <>
                  {/* Deal Summary */}
                  <div className="bg-white border border-slate-200 p-6" data-testid="intel-deal-summary">
                    <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-3">Deal Summary</h3>
                    <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{intel.deal_summary}</p>
                    {lead.website && <p className="text-xs text-slate-400 font-['JetBrains_Mono'] mt-3">Source: {lead.website}</p>}
                  </div>

                  {/* Priority Opportunities */}
                  <div className="bg-white border border-slate-200 p-6" data-testid="intel-opportunities">
                    <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-4">Priority Opportunities</h3>
                    <div className="space-y-3">
                      {(intel.priority_opportunities || []).map((opp, i) => {
                        const isRich = typeof opp === "object";
                        return (
                          <div key={`idx-${i}`} className="border border-slate-100 p-4 hover:border-slate-200 transition-colors">
                            <div className="flex items-start justify-between gap-3 mb-1">
                              <div className="flex items-start gap-3">
                                <span className="w-6 h-6 bg-[#0033CC] text-white flex items-center justify-center text-xs font-['JetBrains_Mono'] flex-shrink-0 mt-0.5">{i + 1}</span>
                                <h4 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{isRich ? opp.title : opp}</h4>
                              </div>
                              {isRich && opp.impact && (
                                <span className={`text-[10px] font-['JetBrains_Mono'] px-2 py-0.5 flex-shrink-0 ${opp.impact === "High" ? "bg-red-50 text-red-700" : opp.impact === "Medium" ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700"}`}>{opp.impact}</span>
                              )}
                            </div>
                            {isRich && opp.description && <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed ml-9">{opp.description}</p>}
                            {isRich && opp.estimated_value && <p className="text-xs font-['JetBrains_Mono'] text-emerald-600 ml-9 mt-1">{opp.estimated_value}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sales Pitch Playbook */}
                  <div className="bg-[#0F172A] p-6 text-white" data-testid="intel-pitch">
                    <h3 className="text-base font-['Outfit'] font-medium mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#818CF8]" />Sales Pitch Playbook</h3>
                    {intel.recommended_pitch && typeof intel.recommended_pitch === "object" ? (
                      <div className="space-y-4">
                        <div>
                          <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-slate-500 mb-1">Opening Hook</p>
                          <p className="text-sm font-['IBM_Plex_Sans'] text-slate-200 leading-relaxed italic">"{intel.recommended_pitch.opening_hook}"</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-slate-500 mb-1">Value Proposition</p>
                          <p className="text-sm font-['IBM_Plex_Sans'] text-slate-200 leading-relaxed">{intel.recommended_pitch.value_proposition}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-slate-500 mb-1">Proof Point</p>
                          <p className="text-sm font-['IBM_Plex_Sans'] text-slate-300 leading-relaxed">{intel.recommended_pitch.proof_point}</p>
                        </div>
                        <div className="bg-white/10 p-3 mt-2">
                          <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#818CF8] mb-1">Close With</p>
                          <p className="text-sm font-['IBM_Plex_Sans'] text-white font-medium">"{intel.recommended_pitch.closing_question}"</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm font-['IBM_Plex_Sans'] text-slate-300 leading-relaxed">{intel.recommended_pitch_angle || intel.recommended_pitch || ""}</p>
                    )}
                  </div>

                  {/* Implementation Roadmap */}
                  <div className="bg-white border border-slate-200 p-6" data-testid="intel-implementation">
                    <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-4">Implementation Roadmap</h3>
                    {intel.implementation_plan && typeof intel.implementation_plan === "object" ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {["phase_1", "phase_2", "phase_3"].map((key) => {
                          const phase = intel.implementation_plan[key];
                          if (!phase) return null;
                          const colors = { phase_1: "border-t-emerald-500", phase_2: "border-t-[#0033CC]", phase_3: "border-t-purple-500" };
                          return (
                            <div key={key} className={`border border-slate-200 border-t-2 ${colors[key]} p-4`}>
                              <h4 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-2">{phase.title}</h4>
                              <ul className="space-y-1.5 mb-3">
                                {(phase.actions || []).map((a, j) => (
                                  <li key={j} className="text-xs text-slate-600 font-['IBM_Plex_Sans'] flex items-start gap-1.5">
                                    <span className="text-[#0033CC] mt-0.5">+</span>{a}
                                  </li>
                                ))}
                              </ul>
                              {phase.expected_outcome && (
                                <div className="bg-slate-50 p-2 border-l-2 border-emerald-400">
                                  <p className="text-[10px] font-['JetBrains_Mono'] text-emerald-700">{phase.expected_outcome}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{intel.implementation_plan || ""}</p>
                    )}
                  </div>

                  {/* Bottom cards: Deal Size + Risk + Urgency + Next Steps */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white border border-slate-200 p-5">
                      <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-2">Est. Deal Size</h4>
                      <p className="text-xl font-['Outfit'] font-medium text-[#0F172A]">${intel.estimated_deal_size?.low?.toLocaleString()} – ${intel.estimated_deal_size?.high?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500 mt-1 font-['IBM_Plex_Sans']">{intel.estimated_deal_size?.rationale}</p>
                    </div>
                    <div className="bg-white border border-slate-200 p-5">
                      <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-2">Risk Flags</h4>
                      {(intel.risk_flags || []).map((r, i) => (
                        <div key={`idx-${i}`} className="flex items-start gap-2 text-xs text-red-600 font-['IBM_Plex_Sans'] mb-1"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />{typeof r === "string" ? r : r.flag || JSON.stringify(r)}</div>
                      ))}
                    </div>
                    <div className="bg-white border border-slate-200 p-5">
                      <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-2">Urgency Signals</h4>
                      {(intel.urgency_signals || []).map((u, i) => (
                        <div key={`idx-${i}`} className="flex items-start gap-2 text-xs text-amber-700 font-['IBM_Plex_Sans'] mb-1"><Flame className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-500" />{u}</div>
                      ))}
                      {!intel.urgency_signals && intel.urgency_level && (
                        <span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 ${intel.urgency_level === "high" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{intel.urgency_level}</span>
                      )}
                    </div>
                    <div className="bg-white border border-slate-200 p-5">
                      <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-2">Next Steps</h4>
                      {(intel.next_steps || []).map((s, i) => (
                        <div key={`idx-${i}`} className="flex items-start gap-2 text-xs text-slate-600 font-['IBM_Plex_Sans'] mb-1"><span className="text-[#0033CC] font-bold">{i + 1}.</span>{s}</div>
                      ))}
                    </div>
                  </div>

                  <Button onClick={generateAI} disabled={aiLoading} variant="outline" className="rounded-none font-['Outfit']" data-testid="regenerate-ai-btn">
                    {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />} Regenerate Intelligence
                  </Button>
                </>
              )}
            </div>
          </TabsContent>

          {/* COMPETITORS */}
          <TabsContent value="competitors">
            <div className="space-y-6" data-testid="lead-competitors">
              {(!intel?.competitor_analysis) ? (
                <div className="bg-white border border-slate-200 p-10 text-center">
                  <Crosshair className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-['Outfit'] text-[#0F172A] mb-2">Generate Competitor Intelligence</h3>
                  <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-1">AI will identify competitors based on their industry, website, and business description.</p>
                  {lead.website && <p className="text-xs text-[#0033CC] font-['JetBrains_Mono'] mb-4">{lead.website}</p>}
                  <Button onClick={generateCompetitors} disabled={compLoading} className="bg-[#0033CC] text-white rounded-none px-8 font-['Outfit']" data-testid="generate-competitors-btn">
                    {compLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyzing competitors...</> : <><Crosshair className="w-4 h-4 mr-2" />Identify Competitors</>}
                  </Button>
                </div>
              ) : (
                <>
                  {/* Competitor Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(intel.competitor_analysis.competitors || []).map((c, i) => (
                      <div key={`idx-${i}`} className={`bg-white border p-5 ${c.threat_level === "High" ? "border-red-200" : c.threat_level === "Medium" ? "border-amber-200" : "border-slate-200"}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">{c.name}</h4>
                          <span className={`text-[10px] font-['JetBrains_Mono'] px-2 py-0.5 ${c.threat_level === "High" ? "bg-red-50 text-red-700" : c.threat_level === "Medium" ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700"}`}>{c.threat_level} Threat</span>
                        </div>
                        <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed mb-3">{c.description}</p>
                        <div className="bg-[#E5EDFF] p-3">
                          <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#0033CC] mb-1">Our AI Advantage</p>
                          <p className="text-xs text-[#0F172A] font-['IBM_Plex_Sans'] font-medium">{c.ai_advantage}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Positioning Strategy */}
                  <div className="bg-[#0F172A] p-6 text-white">
                    <h3 className="text-base font-['Outfit'] font-medium mb-3 flex items-center gap-2"><Crosshair className="w-4 h-4 text-[#818CF8]" />Positioning Strategy</h3>
                    <p className="text-sm font-['IBM_Plex_Sans'] text-slate-200 leading-relaxed">{intel.competitor_analysis.positioning_strategy}</p>
                  </div>

                  {/* Competitive Moat */}
                  <div className="bg-white border border-slate-200 p-6">
                    <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-3">Competitive Moat</h3>
                    <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{intel.competitor_analysis.competitive_moat}</p>
                  </div>

                  <Button onClick={generateCompetitors} disabled={compLoading} variant="outline" className="rounded-none font-['Outfit']" data-testid="regenerate-competitors-btn">
                    {compLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Crosshair className="w-3 h-3 mr-1" />} Refresh Competitor Analysis
                  </Button>
                </>
              )}
            </div>
          </TabsContent>

          {/* DISCOVERY ANSWERS */}
          <TabsContent value="discovery">
            <div data-testid="lead-discovery-answers">
              {lead.audit_id ? (
                <BlueprintAnswersEditor auditId={lead.audit_id} auditData={lead.audit_data} onUpdate={loadLead} />
              ) : (
                <div className="bg-white border border-slate-200 p-10 text-center">
                  <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-2">No Discovery Started</p>
                  <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mb-6 max-w-md mx-auto">This lead hasn't started a blueprint discovery yet. You can start the discovery on their behalf — their answers will be saved and linked to this lead.</p>
                  <Button onClick={async () => {
                    try {
                      const { data } = await api.post(`/audit/admin/create-for-lead/${leadId}`);
                      if (data.audit_id) {
                        window.open(`/blueprint?audit_id=${data.audit_id}`, "_blank");
                        loadLead();
                      }
                    } catch (err) { alert(err.response?.data?.detail || "Failed to create discovery"); }
                  }} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-6 font-['Outfit'] text-sm" data-testid="start-discovery-for-lead-btn">
                    <Sparkles className="w-4 h-4 mr-2" />Start Discovery for {lead.name?.split(" ")[0] || "Lead"}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* BLUEPRINT DATA */}
          <TabsContent value="audit">
            <div data-testid="lead-audit-data">
              {/* Blueprint Status Banner */}
              {lead.audit_id && lead.audit_data && !lead.audit_data.report && (
                <div className={`mb-4 p-4 flex items-center justify-between ${lead.audit_data.status === "in_progress" ? "bg-amber-50 border border-amber-200" : lead.audit_data.status === "completed" ? "bg-blue-50 border border-blue-200" : "bg-slate-50 border border-slate-200"}`} data-testid="blueprint-status-banner">
                  <div>
                    <p className="text-sm font-['Outfit'] font-medium text-[#0F172A]">
                      {lead.audit_data.status === "in_progress" ? "Blueprint In Progress" : lead.audit_data.status === "completed" ? "Discovery Completed — Report Pending" : `Status: ${lead.audit_data.status}`}
                    </p>
                    <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']">
                      {lead.audit_data.status === "in_progress" ? `${lead.audit_data.progress || 0}% complete` : "Discovery answers submitted"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => window.open(`/blueprint?audit_id=${lead.audit_id}`, "_blank")} variant="outline" size="sm" className="rounded-none h-8 text-xs font-['Outfit'] border-[#0033CC] text-[#0033CC] hover:bg-[#0033CC] hover:text-white" data-testid="open-blueprint-btn">
                      {lead.audit_data.status === "in_progress" ? "Continue Blueprint" : "View Blueprint"}
                    </Button>
                    {lead.audit_data.status === "completed" && (
                      <Button onClick={generateReport} disabled={reportGenerating} size="sm" className="rounded-none h-8 text-xs font-['Outfit'] bg-[#0033CC] text-white" data-testid="gen-report-banner-btn">
                        {reportGenerating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}Generate Report
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {lead.audit_data ? (
                <div className="space-y-6">
                  {/* Top row: Scores + Download */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                      <div className="bg-white border border-slate-200 p-4"><p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Score</p><p className="text-2xl font-['Outfit'] font-light text-[#0033CC]">{scores.overall || 0}/100</p></div>
                      <div className="bg-white border border-slate-200 p-4"><p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Tier</p><p className="text-lg font-['Outfit'] font-medium">{scores.tier || "—"}</p></div>
                      <div className="bg-white border border-slate-200 p-4"><p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Revenue Leak</p><p className="text-lg font-['Outfit'] font-medium">{scores.revenue_leak_severity || "—"}</p></div>
                      <div className="bg-white border border-slate-200 p-4"><p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Op. Drag</p><p className="text-lg font-['Outfit'] font-medium">{scores.operational_drag || "—"}</p></div>
                    </div>
                    {lead.audit_id && (
                      <div className="flex gap-2 flex-shrink-0">
                        {lead.audit_data.report ? (
                          <>
                            <Button onClick={downloadPDF} disabled={pdfLoading} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-4 font-['Outfit'] text-sm" data-testid="audit-download-pdf">
                              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}Report PDF
                            </Button>
                            <Button onClick={downloadDeck} disabled={deckLoading} variant="outline" className="border-[#0033CC] text-[#0033CC] hover:bg-[#0033CC] hover:text-white rounded-none h-10 px-4 font-['Outfit'] text-sm transition-colors" data-testid="audit-download-deck">
                              {deckLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}Exec Deck
                            </Button>
                            <Button onClick={downloadProposal} disabled={proposalLoading} variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-none h-10 px-4 font-['Outfit'] text-sm transition-colors" data-testid="generate-proposal-btn">
                              {proposalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}Proposal
                            </Button>
                            <Button onClick={() => { if (window.confirm("Regenerate the entire blueprint report? This will replace the current report with a fresh AI-generated version.")) generateReport(); }} disabled={reportGenerating} variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white rounded-none h-10 px-4 font-['Outfit'] text-sm transition-colors" data-testid="regenerate-report-btn">
                              {reportGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}Regenerate
                            </Button>
                          </>
                        ) : (
                          <div className="flex gap-2">
                            <Button onClick={generateReport} disabled={reportGenerating} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-4 font-['Outfit'] text-sm" data-testid="generate-report-btn">
                              {reportGenerating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Blueprint</>}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Discovery Scores (V2 12-section) */}
                  <div className="bg-white border border-slate-200 p-6">
                    <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Discovery Scores</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5">
                      {Object.entries(sections).filter(([k]) => !["acquire","convert","operate","expand","defend","readiness"].includes(k)).map(([k, v]) => (
                        <div key={k} className="flex items-center gap-3">
                          <span className="text-xs text-slate-500 w-36 capitalize font-['IBM_Plex_Sans'] truncate">{k.replace(/_/g, " ")}</span>
                          <div className="flex-1 h-2 bg-slate-100"><div className="h-full bg-[#0033CC]" style={{ width: `${v.percentage}%` }} /></div>
                          <span className="text-xs font-['JetBrains_Mono'] w-10 text-right">{v.percentage}%</span>
                        </div>
                      ))}
                    </div>
                    {/* Revenue Leak Summary */}
                    {scores.revenue_leak_analysis && (
                      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-4">
                        <div><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-red-500 mb-0.5">Est. Annual Leakage</p><p className="text-sm font-['Outfit'] font-medium text-red-600">{scores.revenue_leak_analysis.estimated_annual_leakage}</p></div>
                        <div><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 mb-0.5">Weekly Hours Wasted</p><p className="text-sm font-['Outfit'] font-medium">{scores.revenue_leak_analysis.estimated_weekly_hours_wasted}hrs</p></div>
                        <div><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 mb-0.5">Monthly Opportunity Cost</p><p className="text-sm font-['Outfit'] font-medium">{scores.revenue_leak_analysis.opportunity_cost}</p></div>
                      </div>
                    )}
                  </div>

                  {/* V2 Report Summary Cards */}
                  {lead.audit_data.report ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-[#0033CC]" />
                        <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Transformation Blueprint</h3>
                      </div>

                      {/* Executive Snapshot */}
                      <div className="bg-white border border-slate-200 p-6">
                        <h4 className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-3">Executive Snapshot</h4>
                        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{lead.audit_data.report.executive_snapshot?.company_overview || lead.audit_data.report.executive_summary || ""}</p>
                        {lead.audit_data.report.executive_snapshot?.headline_finding && (
                          <div className="mt-3 bg-[#0F172A] p-4"><p className="text-sm text-white font-['Outfit'] font-medium">"{lead.audit_data.report.executive_snapshot.headline_finding}"</p></div>
                        )}
                      </div>

                      {/* Revenue Leak Analysis */}
                      {lead.audit_data.report.revenue_leak_analysis && (
                        <div className="bg-white border border-slate-200 p-6">
                          <h4 className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-red-600 mb-3">Revenue Leak Analysis</h4>
                          <div className="text-2xl font-['Outfit'] font-light text-red-600 mb-3">{lead.audit_data.report.revenue_leak_analysis.total_estimated_leakage}</div>
                          <div className="space-y-2">
                            {(lead.audit_data.report.revenue_leak_analysis.leak_categories || []).map((lk, i) => (
                              <div key={`lk-${i}`} className="flex items-center gap-3 text-xs font-['IBM_Plex_Sans'] py-1.5 border-b border-slate-50">
                                <span className={`font-['JetBrains_Mono'] px-1.5 py-0.5 ${lk.severity === "High" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{lk.severity}</span>
                                <span className="flex-1 text-slate-700">{lk.category}</span>
                                <span className="font-['JetBrains_Mono'] text-red-600">{lk.estimated_annual_loss}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Opportunity Matrix */}
                      {lead.audit_data.report.opportunity_matrix && (
                        <div className="bg-white border border-slate-200 p-6">
                          <h4 className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-3">AI Opportunity Matrix</h4>
                          <div className="space-y-2">
                            {(lead.audit_data.report.opportunity_matrix.opportunities || []).map((o, i) => (
                              <div key={`om-${i}`} className="flex items-center gap-3 text-xs py-2 border-b border-slate-50">
                                <span className={`font-['JetBrains_Mono'] px-1.5 py-0.5 text-white ${o.priority === "P1" ? "bg-[#0033CC]" : "bg-slate-400"}`}>{o.priority}</span>
                                <span className="font-['Outfit'] font-medium text-[#0F172A] flex-1">{o.initiative}</span>
                                <span className="text-slate-400">{o.department}</span>
                                <span className="font-['JetBrains_Mono'] text-emerald-600">{o.revenue_impact}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Department Transformation */}
                      {lead.audit_data.report.department_transformation && (
                        <div className="bg-white border border-slate-200 p-6">
                          <h4 className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-3">Department Transformation Plans</h4>
                          <div className="space-y-3">
                            {(lead.audit_data.report.department_transformation.departments || []).map((dept, i) => (
                              <div key={`dept-${i}`} className="border border-slate-100 p-4">
                                <h5 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-2">{dept.name}</h5>
                                <div className="grid grid-cols-2 gap-3">
                                  <div><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-red-500 mb-1">Current</p><p className="text-xs text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed line-clamp-3">{dept.current_state}</p></div>
                                  <div><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-emerald-600 mb-1">Future</p><p className="text-xs text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed line-clamp-3">{dept.future_state}</p></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ROI Model */}
                      {lead.audit_data.report.roi_model && (
                        <div className="bg-[#0F172A] p-6">
                          <h4 className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#818CF8] mb-3">ROI Model</h4>
                          <div className="grid grid-cols-4 gap-3 mb-3">
                            {Object.entries(lead.audit_data.report.roi_model.economics || {}).map(([k, v]) => (
                              <div key={k} className="text-center">
                                <p className="text-[10px] text-slate-400 mb-0.5">{k.replace(/_/g, " ")}</p>
                                <p className="text-lg font-['Outfit'] font-light text-white">{String(v)}</p>
                              </div>
                            ))}
                          </div>
                          {lead.audit_data.report.roi_model.executive_summary && <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] leading-relaxed border-t border-white/10 pt-3">{lead.audit_data.report.roi_model.executive_summary}</p>}
                        </div>
                      )}

                      {/* Implementation Roadmap */}
                      {lead.audit_data.report.implementation_roadmap && (
                        <div className="bg-white border border-slate-200 p-6">
                          <h4 className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-3">Implementation Roadmap</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {["phase_1","phase_2","phase_3"].map((pk, i) => {
                              const phase = lead.audit_data.report.implementation_roadmap[pk];
                              if (!phase) return null;
                              const colors = ["border-t-emerald-500","border-t-[#0033CC]","border-t-purple-500"];
                              const actions = (phase.actions || []).slice(0, 5);
                              return (
                                <div key={pk} className={`border border-slate-200 border-t-2 ${colors[i]} p-4`}>
                                  <h5 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-1">{phase.title || `Phase ${i+1}`}</h5>
                                  <p className="text-[10px] text-slate-400 font-['JetBrains_Mono'] mb-2">{phase.timeline}</p>
                                  <ul className="space-y-1">
                                    {actions.map((a, j) => <li key={`ra-${j}`} className="text-xs text-slate-600 font-['IBM_Plex_Sans']">+ {typeof a === "object" ? a.action : String(a)}</li>)}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white border border-slate-200 p-8 text-center">
                      <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-2">Transformation Blueprint Not Yet Generated</p>
                      <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mb-4">Generate the full transformation blueprint to view revenue leaks, AI opportunities, department plans, and ROI projections.</p>
                      <Button onClick={generateReport} disabled={reportGenerating} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-6 h-10 font-['Outfit'] text-sm" data-testid="generate-report-inline-btn">
                        {reportGenerating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Transformation Blueprint</>}
                      </Button>
                    </div>
                  )}
                  {/* Report Section Editor */}
                  {lead.audit_data?.report && lead.audit_id && (
                    <div className="mt-6" data-testid="report-editor-section">
                      <ReportEditor auditId={lead.audit_id} report={lead.audit_data.report} onUpdate={loadLead} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-slate-200 p-10 text-center">
                  <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-2">No Blueprint Data Yet</p>
                  <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mb-4">This lead hasn't completed a blueprint yet. Use the Invite Client tool to send them an onboarding invitation.</p>
                  {lead.audit_id && (
                    <Button onClick={generateReport} disabled={reportGenerating} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-6 h-10 font-['Outfit'] text-sm" data-testid="generate-report-no-data-btn">
                      {reportGenerating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Report Anyway</>}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* CALLS */}
          <TabsContent value="calls">
            <div className="space-y-4" data-testid="lead-calls">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Call History</h3>
                <Button onClick={() => setShowCallForm(!showCallForm)} size="sm" className="rounded-none bg-[#0033CC] text-white text-xs font-['Outfit']" data-testid="add-call-btn"><Plus className="w-3 h-3 mr-1" />Log Call</Button>
              </div>
              {showCallForm && (
                <div className="bg-white border border-slate-200 p-4 space-y-3" data-testid="call-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Date</Label><Input type="date" value={callForm.date} onChange={e => setCallForm({ ...callForm, date: e.target.value })} className="rounded-none border-slate-300 h-8 text-sm" data-testid="call-date" /></div>
                    <div><Label className="text-xs">Type</Label>
                      <Select value={callForm.call_type} onValueChange={v => setCallForm({ ...callForm, call_type: v })}>
                        <SelectTrigger className="rounded-none border-slate-300 h-8 text-sm" data-testid="call-type"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="discovery">Discovery</SelectItem><SelectItem value="strategy">Strategy</SelectItem><SelectItem value="follow_up">Follow-up</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Textarea value={callForm.notes} onChange={e => setCallForm({ ...callForm, notes: e.target.value })} placeholder="Call notes..." className="rounded-none border-slate-300 min-h-[60px] text-sm" data-testid="call-notes" />
                  <Button onClick={addCall} size="sm" className="rounded-none bg-[#0033CC] text-white text-xs font-['Outfit']" data-testid="save-call-btn">Schedule Call</Button>
                </div>
              )}
              {(lead.calls || []).map(c => (
                <div key={c.id} className="bg-white border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-['JetBrains_Mono'] text-slate-500">{c.date}</span>
                      <span className="text-xs font-['JetBrains_Mono'] px-1.5 py-0.5 bg-slate-100 text-slate-600 capitalize">{c.call_type?.replace("_", " ")}</span>
                      <span className={`text-xs font-['JetBrains_Mono'] px-1.5 py-0.5 ${c.status === "completed" ? "bg-green-50 text-green-700" : c.status === "no_show" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`}>{c.status}</span>
                    </div>
                    <Select value={c.outcome || ""} onValueChange={v => updateCall(c.id, { outcome: v, status: "completed" })}>
                      <SelectTrigger className="w-36 rounded-none border-slate-300 h-7 text-xs"><SelectValue placeholder="Set outcome" /></SelectTrigger>
                      <SelectContent><SelectItem value="qualified">Qualified</SelectItem><SelectItem value="not_qualified">Not Qualified</SelectItem><SelectItem value="follow_up">Follow Up</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {c.notes && <p className="text-sm text-slate-600 font-['IBM_Plex_Sans']">{c.notes}</p>}
                </div>
              ))}
              {(!lead.calls || lead.calls.length === 0) && <p className="text-sm text-slate-400 text-center py-8">No calls logged yet</p>}
            </div>
          </TabsContent>

          {/* NOTES */}
          <TabsContent value="notes">
            <div className="space-y-4" data-testid="lead-notes">
              <div className="bg-white border border-slate-200 p-4">
                <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." className="rounded-none border-slate-300 min-h-[80px] text-sm mb-3" data-testid="note-input" />
                <Button onClick={addNote} disabled={savingNote || !noteText.trim()} size="sm" className="rounded-none bg-[#0033CC] text-white text-xs font-['Outfit']" data-testid="add-note-btn">{savingNote ? "Saving..." : "Add Note"}</Button>
              </div>
              {(lead.notes || []).map(n => (
                <div key={n.id} className="bg-white border border-slate-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-['Outfit'] font-medium text-[#0F172A]">{n.author}</span>
                    <span className="text-xs font-['JetBrains_Mono'] text-slate-400">{n.created_at?.split("T")[0]}</span>
                  </div>
                  <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] whitespace-pre-line">{n.content}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* BILLING */}
          <TabsContent value="billing">
            <div className="space-y-4" data-testid="lead-billing">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Payment Records</h3>
                <div className="flex gap-2">
                  <Button onClick={() => setShowBillingForm(!showBillingForm)} size="sm" className="rounded-none bg-[#0033CC] text-white text-xs font-['Outfit']" data-testid="add-billing-btn"><Plus className="w-3 h-3 mr-1" />Add Payment</Button>
                </div>
              </div>
              {showBillingForm && (
                <div className="bg-white border border-slate-200 p-4 grid grid-cols-3 gap-3" data-testid="billing-form">
                  <div><Label className="text-xs">Amount ($)</Label><Input type="number" value={billingForm.amount} onChange={e => setBillingForm({ ...billingForm, amount: e.target.value })} className="rounded-none border-slate-300 h-8 text-sm" data-testid="billing-amount" /></div>
                  <div><Label className="text-xs">Type</Label>
                    <Select value={billingForm.bill_type} onValueChange={v => setBillingForm({ ...billingForm, bill_type: v })}>
                      <SelectTrigger className="rounded-none border-slate-300 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="audit">Blueprint</SelectItem><SelectItem value="implementation">Implementation</SelectItem><SelectItem value="retainer">Retainer</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Status</Label>
                    <Select value={billingForm.status} onValueChange={v => setBillingForm({ ...billingForm, status: v })}>
                      <SelectTrigger className="rounded-none border-slate-300 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="failed">Failed</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addBilling} className="col-span-3 rounded-none bg-[#0033CC] text-white text-xs h-8 font-['Outfit']" data-testid="save-billing-btn">Add Record</Button>
                </div>
              )}
              {(lead.billing || []).map(b => (
                <div key={b.id} className="bg-white border border-slate-200 p-4 flex items-center justify-between">
                  <div>
                    <span className="text-lg font-['Outfit'] font-medium text-[#0F172A]">${b.amount?.toLocaleString()}</span>
                    <span className="text-xs font-['JetBrains_Mono'] ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-600 capitalize">{b.bill_type}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 ${b.status === "paid" ? "bg-green-50 text-green-700" : b.status === "failed" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"}`}>{b.status}</span>
                    <span className="text-xs font-['JetBrains_Mono'] text-slate-400">{b.date?.split("T")[0]}</span>
                  </div>
                </div>
              ))}
              {(!lead.billing || lead.billing.length === 0) && <p className="text-sm text-slate-400 text-center py-8">No payment records</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Close Reason Modal */}
      {closeModal && (
        <CloseReasonModal
          isWin={closeModal.isWin}
          leadName={lead.name}
          company={lead.company}
          onConfirm={handleCloseConfirm}
          onCancel={() => setCloseModal(null)}
        />
      )}

      {/* Comp Blueprint Confirmation */}
      {showCompConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCompConfirm(false)}>
          <div className="bg-white border border-slate-200 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="comp-confirm-modal">
            <div className="p-5 bg-emerald-50 border-b border-emerald-200 flex items-center gap-3">
              <Gift className="w-5 h-5 text-emerald-600" />
              <div>
                <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A]">Comp Blueprint</h3>
                <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']">{lead.name} at {lead.company} — Retail value: $4,495</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600 font-['IBM_Plex_Sans']">This will send a complimentary Transformation Blueprint invitation with payment waived.</p>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Reason for comping *</Label>
                <Textarea value={compReason} onChange={e => setCompReason(e.target.value)} placeholder="e.g., Strategic partnership, referral incentive, high-value prospect..." className="rounded-none border-slate-300 min-h-[70px] text-sm" data-testid="comp-reason-input" />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex gap-3">
              <Button variant="outline" onClick={() => setShowCompConfirm(false)} className="flex-1 rounded-none font-['Outfit']">Cancel</Button>
              <Button onClick={sendCompBlueprint} disabled={compSending || !compReason.trim()} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-['Outfit']" data-testid="comp-confirm-send">
                {compSending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & Send Comp"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white border border-slate-200 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="p-5 bg-red-50 border-b border-red-200 flex items-center gap-3">
              <Trash2 className="w-5 h-5 text-red-500" />
              <div>
                <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A]">Delete Lead</h3>
                <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']">{lead.name} {lead.company ? `at ${lead.company}` : ""}</p>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] mb-1">This will permanently delete this lead and all associated data:</p>
              <ul className="text-xs text-slate-500 font-['IBM_Plex_Sans'] space-y-0.5 ml-4 list-disc mb-4">
                <li>All calls and notes</li>
                <li>Tasks and billing records</li>
                <li>Activity history</li>
                <li>AI intelligence data</li>
              </ul>
              <p className="text-xs text-red-600 font-['IBM_Plex_Sans'] font-medium">This action cannot be undone.</p>
            </div>
            <div className="p-5 border-t border-slate-200 flex gap-3">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="flex-1 rounded-none font-['Outfit']" data-testid="delete-cancel">Cancel</Button>
              <Button onClick={deleteLead} disabled={deleting} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-none font-['Outfit']" data-testid="delete-confirm">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete Permanently"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Proposal Builder Modal */}
      {showProposalBuilder && lead.audit_id && (
        <ProposalBuilder
          auditId={lead.audit_id}
          lead={lead}
          onClose={() => setShowProposalBuilder(false)}
        />
      )}

      {/* Blueprint Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowInviteModal(false)}>
          <div className="bg-white border border-slate-200 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="blueprint-invite-modal">
            <div className="p-5 border-b border-slate-200 flex items-center gap-3">
              <Send className="w-5 h-5 text-[#0033CC]" />
              <div>
                <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A]">Send Blueprint Invite</h3>
                <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{lead.name} &middot; {lead.email}</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">
                Send {lead.name?.split(" ")[0] || "this lead"} a branded email with a unique link to pay and complete their Transformation Blueprint.
              </p>
              <div>
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Price (USD)</label>
                <Input value={invitePrice} onChange={e => setInvitePrice(e.target.value)} placeholder="4495" className="rounded-none border-slate-300 h-9 text-sm" data-testid="invite-price-input" />
                <p className="text-[10px] text-slate-400 mt-1">Enter 0 for complimentary</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Custom Message (Optional)</label>
                <Textarea value={inviteMessage} onChange={e => setInviteMessage(e.target.value)} placeholder="Add a personal note that will appear in the email..." className="rounded-none border-slate-300 text-sm min-h-[60px]" data-testid="invite-message-input" />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex gap-3">
              <Button variant="outline" onClick={() => setShowInviteModal(false)} className="flex-1 rounded-none font-['Outfit']">Cancel</Button>
              <Button onClick={sendBlueprintInvite} disabled={inviteSending} className="flex-1 bg-[#0033CC] text-white hover:bg-[#002299] rounded-none font-['Outfit']" data-testid="send-invite-confirm">
                {inviteSending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Invite</>}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
